import { useState, useRef, useEffect, useCallback } from 'react';
import api from '../../services/api';
import { API } from '../../App';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { ScrollArea } from '../ui/scroll-area';
import { toast } from 'sonner';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  Database, Send, Loader2, Plus, Trash2, Download, Search, MessageSquare, Paperclip, FileSpreadsheet,
} from 'lucide-react';

/**
 * Terra — read-only AI data assistant. Any user with `can_access_ai_assistant`
 * (or super_admin) gets their own private, persisted conversation history here.
 * Streams responses live (SSE) and renders inline download buttons whenever
 * the assistant produces a report file.
 */
export default function TerraAssistantPanel({ user }) {
  const [conversations, setConversations] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [messages, setMessages] = useState([]); // {role, content, downloads?}
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [toolLabel, setToolLabel] = useState(null);
  const [loadingConvo, setLoadingConvo] = useState(false);
  const scrollRef = useRef(null);
  const fileInputRef = useRef(null);

  const loadConversations = useCallback(async () => {
    try {
      const res = await api.get(`${API}/ai-assistant/conversations`);
      setConversations(res.data?.conversations || []);
      return res.data?.conversations || [];
    } catch {
      return [];
    }
  }, []);

  const startNewChat = useCallback(async () => {
    try {
      const res = await api.post(`${API}/ai-assistant/conversations`);
      await loadConversations();
      setActiveId(res.data.id);
      setMessages([]);
    } catch {
      toast.error('Failed to start a new chat');
    }
  }, [loadConversations]);

  const openConversation = async (id) => {
    setLoadingConvo(true);
    setActiveId(id);
    try {
      const res = await api.get(`${API}/ai-assistant/conversations/${id}`);
      setMessages(res.data?.messages || []);
    } catch {
      toast.error('Failed to load that conversation');
    } finally {
      setLoadingConvo(false);
    }
  };

  const deleteConversation = async (id, e) => {
    e.stopPropagation();
    try {
      await api.delete(`${API}/ai-assistant/conversations/${id}`);
      const remaining = await loadConversations();
      if (activeId === id) {
        if (remaining.length > 0) openConversation(remaining[0].id);
        else startNewChat();
      }
    } catch {
      toast.error('Failed to delete conversation');
    }
  };

  useEffect(() => {
    (async () => {
      const convos = await loadConversations();
      if (convos.length > 0) openConversation(convos[0].id);
      else startNewChat();
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages, sending]);

  const sendMessage = async () => {
    const text = input.trim();
    if (!text || sending || !activeId) return;
    setInput('');
    setMessages((prev) => [...prev, { role: 'user', content: text }]);
    setSending(true);
    setToolLabel(null);

    let assistantText = '';
    let downloads = [];
    setMessages((prev) => [...prev, { role: 'assistant', content: '', downloads: [] }]);

    try {
      const token = localStorage.getItem('token');
      const resp = await fetch(`${API}/ai-assistant/conversations/${activeId}/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ message: text }),
      });
      if (!resp.ok || !resp.body) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to reach Terra');
      }

      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let newTitle = null;
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n\n');
        buffer = lines.pop(); // keep any incomplete trailing chunk
        for (const line of lines) {
          if (!line.startsWith('data:')) continue;
          const ev = JSON.parse(line.slice(5).trim());
          if (ev.type === 'delta') {
            assistantText += ev.content;
            setToolLabel(null);
            setMessages((prev) => {
              const next = [...prev];
              next[next.length - 1] = { role: 'assistant', content: assistantText, downloads };
              return next;
            });
          } else if (ev.type === 'tool_start') {
            setToolLabel(_toolFriendlyName(ev.name));
          } else if (ev.type === 'done') {
            downloads = ev.downloads || [];
            newTitle = ev.title;
            setMessages((prev) => {
              const next = [...prev];
              next[next.length - 1] = { role: 'assistant', content: assistantText, downloads };
              return next;
            });
          } else if (ev.type === 'error') {
            throw new Error(ev.detail || 'Something went wrong');
          }
        }
      }
      if (newTitle) {
        setConversations((prev) => prev.map((c) => (c.id === activeId ? { ...c, title: newTitle } : c)));
      }
    } catch (e) {
      toast.error(e.message || 'Failed to reach Terra');
      setMessages((prev) => {
        const next = [...prev];
        next[next.length - 1] = { role: 'assistant', content: assistantText || 'Something went wrong reaching Terra — see the toast for details.', downloads };
        return next;
      });
    } finally {
      setSending(false);
      setToolLabel(null);
    }
  };

  const handleFileSelected = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !activeId) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post(`${API}/ai-assistant/conversations/${activeId}/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const { filename, summary } = res.data;
      setMessages((prev) => [...prev, {
        role: 'upload',
        content: `Uploaded "${filename}" — ${summary}`,
      }]);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to upload that file');
    } finally {
      setUploading(false);
    }
  };

  const downloadFile = async (url, filename) => {
    try {
      const res = await api.get(url, { responseType: 'blob' });
      const blobUrl = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch {
      toast.error('Failed to download file');
    }
  };

  return (
    <div className="space-y-3" data-testid="terra-assistant-panel">
      <div>
        <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <Database className="w-5 h-5" style={{ color: '#ee7203' }} />
          Terra
        </h2>
        <p className="text-sm text-gray-500 mt-1">
          Ask Terra questions about your dashboard data — sales figures, projects, what people
          are quoting, Barbour ABI project search, and more. Upload any spreadsheet, PDF, or Word
          document (paperclip button) and Terra reads it directly — for a spreadsheet with a
          project name + postcode, it will also offer to match each row to a real Barbour ID and,
          only when you explicitly ask, push the matches onto the linked CRM records. Otherwise
          fully read-only: it cannot create, edit, or delete anything else.
        </p>
      </div>

      <div className="flex gap-3 h-[560px]">
        {/* Conversation sidebar */}
        <div className="w-56 flex-shrink-0 border rounded-lg flex flex-col overflow-hidden bg-gray-50">
          <div className="p-2 border-b bg-white">
            <Button
              size="sm"
              variant="outline"
              className="w-full justify-start text-xs"
              onClick={startNewChat}
              data-testid="terra-new-chat-btn"
            >
              <Plus className="w-3.5 h-3.5 mr-1.5" /> New chat
            </Button>
          </div>
          <ScrollArea className="flex-1">
            <div className="p-1.5 space-y-1">
              {conversations.length === 0 && (
                <div className="text-xs text-gray-400 text-center py-6">No conversations yet</div>
              )}
              {conversations.map((c) => (
                <div
                  key={c.id}
                  onClick={() => openConversation(c.id)}
                  data-testid={`terra-conversation-${c.id}`}
                  className={`group flex items-center gap-1.5 px-2 py-2 rounded-md cursor-pointer text-xs ${
                    activeId === c.id ? 'text-white' : 'hover:bg-gray-200 text-gray-700'
                  }`}
                  style={activeId === c.id ? { backgroundColor: '#ee7203' } : undefined}
                >
                  <MessageSquare className="w-3 h-3 flex-shrink-0" />
                  <span className="truncate flex-1">{c.title || 'New chat'}</span>
                  <button
                    onClick={(e) => deleteConversation(c.id, e)}
                    data-testid={`terra-delete-conversation-${c.id}`}
                    className={`opacity-0 group-hover:opacity-100 flex-shrink-0 ${activeId === c.id ? 'text-white/80 hover:text-white' : 'text-gray-400 hover:text-red-500'}`}
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
          </ScrollArea>
        </div>

        {/* Chat window */}
        <div className="flex-1 border rounded-lg flex flex-col overflow-hidden">
          <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3">
            {loadingConvo ? (
              <div className="flex justify-center py-12"><Loader2 className="w-5 h-5 animate-spin text-gray-300" /></div>
            ) : messages.length === 0 ? (
              <div className="text-sm text-gray-400 text-center py-12 space-y-1">
                <div>Ask about your data — for example:</div>
                <div className="italic">"What's our project pipeline breakdown by stage?"</div>
                <div className="italic">"What has the team quoted this week?"</div>
                <div className="italic">"Find Barbour projects within 10 miles of WR14 3HB"</div>
                <div className="pt-1 flex items-center justify-center gap-1 text-gray-400">
                  <Paperclip className="w-3 h-3" /> Or upload a spreadsheet, PDF, or Word doc — Terra reads it directly
                </div>
              </div>
            ) : (
              messages.map((m, idx) => (
                m.role === 'upload' ? (
                  <div key={idx} className="flex justify-center" data-testid={`terra-upload-marker-${idx}`}>
                    <div className="max-w-[90%] rounded-lg px-3 py-2 text-xs bg-cyan-50 text-cyan-800 border border-cyan-200 flex items-center gap-2">
                      <FileSpreadsheet className="w-3.5 h-3.5 flex-shrink-0" />
                      {m.content}
                    </div>
                  </div>
                ) : (
                <div key={idx} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div
                    className={`max-w-[85%] rounded-lg px-3 py-2 text-sm ${
                      m.role === 'user' ? 'text-white whitespace-pre-wrap' : 'bg-gray-100 text-gray-800 terra-markdown'
                    }`}
                    style={m.role === 'user' ? { backgroundColor: '#ee7203' } : undefined}
                    data-testid={`terra-message-${idx}`}
                  >
                    {m.role === 'user' ? (
                      m.content
                    ) : m.content ? (
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>{m.content}</ReactMarkdown>
                    ) : (
                      sending && idx === messages.length - 1 ? '…' : ''
                    )}
                    {(m.downloads || []).map((d, di) => (
                      <button
                        key={di}
                        onClick={() => downloadFile(d.url, d.filename)}
                        data-testid={`terra-download-${idx}-${di}`}
                        className="mt-2 flex items-center gap-1.5 text-xs font-medium px-2.5 py-1.5 rounded-md border border-orange-300 text-orange-700 bg-orange-50 hover:bg-orange-100 transition-colors"
                      >
                        <Download className="w-3.5 h-3.5" /> {d.filename}
                      </button>
                    ))}
                  </div>
                </div>
                )
              ))
            )}
            {sending && toolLabel && (
              <div className="flex justify-start">
                <div className="bg-gray-100 rounded-lg px-3 py-2 text-sm text-gray-400 flex items-center gap-2">
                  <Search className="w-3.5 h-3.5" /> {toolLabel}
                </div>
              </div>
            )}
          </div>
          <div className="border-t p-3 flex gap-2">
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls,.csv,.pdf,.docx"
              onChange={handleFileSelected}
              className="hidden"
              data-testid="terra-file-input"
            />
            <Button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading || sending}
              size="icon"
              variant="outline"
              title="Upload a spreadsheet, PDF, or Word document for Terra to read"
              data-testid="terra-upload-btn"
            >
              {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Paperclip className="w-4 h-4" />}
            </Button>
            <Input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
              placeholder="Ask Terra about your data..."
              disabled={sending}
              className="text-sm"
              data-testid="terra-assistant-input"
            />
            <Button
              onClick={sendMessage}
              disabled={sending || !input.trim()}
              size="icon"
              style={{ backgroundColor: '#ee7203', color: 'white' }}
              data-testid="terra-assistant-send-btn"
            >
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function _toolFriendlyName(name) {
  const map = {
    get_po_spend_summary: 'Checking P.O. spend...',
    list_po_expenses: 'Looking up P.O. expenses...',
    get_sales_figures: 'Pulling sales figures...',
    list_recent_quotes: 'Checking recent quotes...',
    get_projects_summary: 'Checking the project pipeline...',
    generate_download: 'Putting your file together...',
    find_barbour_ids: 'Searching Barbour ABI for matching projects...',
    push_barbour_ids_to_crm: 'Updating CRM records...',
    search_barbour_projects: 'Searching Barbour ABI...',
  };
  return map[name] || 'Looking that up...';
}
